import { memo, useCallback } from "react"
import { FlatList, ListRenderItemInfo, Platform, StyleSheet, View } from "react-native"
import { ContactItem } from "src/components/parts/ContactItem"
import { ContactListEmpty } from "src/components/parts/contactNetworkTab/ContactListEmpty"
import { DrawerStatus } from "src/components/parts/contactNetworkTab/DrawerStatus"
import { DraftIcon } from "src/components/parts/icons/drawer/DraftIcon"
import { CustomRefreshControl } from "src/components/projects/CustomRefreshControl"
import { Colors } from "src/constants/Colors"
import { ContactNetworkDrawers } from "src/constants/ContactNetworkDrawers"
import { CreateNewContactScreens } from "src/constants/CreateNewContactScreens"
import { Screens } from "src/constants/Screens"
import { DrawerScreenOptions } from "src/navigation/ContactNetworkDrawer"
import { useDraftData } from "src/recoil/hooks/screens/useDraftData"
import type { ContactNetworkDrawerProps } from "src/types.d"
import { ContactOverviewModel } from "src/types/contact/ContactOverviewModel"
import { DrawerIconContainer } from "src/components/parts/contactNetworkTab/DrawerIconContainer"
import { CustomAlert } from "src/utils/CustomAlert"
import { useContactNetworkDrawerNavEffect } from "src/recoil/hooks/contact/useContactNetworkDrawerNavEffect"
import { RootTabs } from "src/constants/RootTabs"
import { Analytics } from "src/tags/analytics/Analytics"
import { adLoadState, isLoadDoneAd } from "src/recoil/atoms/ads/adState"
import { useRecoilState } from "recoil"
import { MainLayout } from "src/layouts/MainLayout"
import { DrawerLabel } from "src/components/parts/contactNetworkTab/DrawerLabel"
import { useFetchBanner } from "src/hooks/useFetchBanner"
import { useAdTargetingData } from "src/hooks/useAdTargetingData"
export type DraftScreenParams = {
  organizationId?: string
}

export const DraftScreen = memo<ContactNetworkDrawerProps<typeof ContactNetworkDrawers.DraftScreen>>(
  ({ navigation, route }) => {
    const {
      contacts,
      isLoadingContacts,
      refreshContacts,
      execDeleteContactDraft,
      resetCreateNewContactScreenData,
      organizationId,
      organization,
      refreshOrganizationDetail,
    } = useDraftData()
    const { data: adTargetingData } = useAdTargetingData()
    const [adLoadStateList] = useRecoilState(adLoadState)
    const homeBottomBanner9 = useFetchBanner("HomeBottom_9", adTargetingData)
    const homeBottomBanner10 = useFetchBanner("HomeBottom_10", adTargetingData)

    // web版のリロードに対応するため、クエリパラメータを付与してリダイレクト
    useContactNetworkDrawerNavEffect({
      queryParamOrganizationId: route.params?.organizationId,
      screenName: ContactNetworkDrawers.DraftScreen,
      redirect: (organizationId) => {
        navigation.replace(Screens.Root, {
          screen: RootTabs.ContactNetworkDrawer,
          params: {
            screen: ContactNetworkDrawers.DraftScreen,
            params: { organizationId: organizationId },
          },
        })
      },
    })

    const gotoCreateNewContactScreen = useCallback(
      async (draftId: string) => {
        if (organizationId != null) await Analytics.goToCreateMail(organizationId)
        resetCreateNewContactScreenData({ mode: "New", draftId })
        CustomAlert.alert(
          "警告",
          "下書き保存時に添付ファイルは保存されていません。\nファイル添付をされていた場合は、連絡送信時に再度添付してください。"
        )
        navigation.navigate(Screens.CreateNewContactStack, { screen: CreateNewContactScreens.CreateNewContact })
      },
      [navigation, resetCreateNewContactScreenData, organizationId]
    )

    const ListItem = useCallback(
      (listRenderItemInfo: ListRenderItemInfo<ContactOverviewModel>) => {
        return (
          <View style={styles.contentWrapper}>
            <ContactItem
              contactId={listRenderItemInfo.item.id}
              viewType="Draft"
              title={listRenderItemInfo.item.title}
              contactType={listRenderItemInfo.item.contactType}
              hasAttachment={listRenderItemInfo.item.isAttachmentExists}
              onContactPress={() => gotoCreateNewContactScreen(listRenderItemInfo.item.id)}
              onDeletePress={() => execDeleteContactDraft(listRenderItemInfo.item.id)}
              isEdited={listRenderItemInfo.item.isUpdate}
              updatedDate={listRenderItemInfo.item.updatedDate || new Date()}
              noBorder
              unreadThreadCount={0}
            />
            {Platform.OS !== "web" &&
            (!organization?.paidFunctionEnabled || organization?.showAdsFlg) &&
            listRenderItemInfo.index === 2 ? (
              <View style={!isLoadDoneAd("home_bottom", adLoadStateList, 9) && { height: 0 }}>{homeBottomBanner9}</View>
            ) : null}
            {Platform.OS !== "web" &&
            (!organization?.paidFunctionEnabled || organization?.showAdsFlg) &&
            listRenderItemInfo.index === 5 ? (
              <View style={!isLoadDoneAd("home_bottom", adLoadStateList, 10) && { height: 0 }}>{homeBottomBanner10}</View>
            ) : null}
          </View>
        )
      },
      [adLoadStateList, gotoCreateNewContactScreen, execDeleteContactDraft, organization, homeBottomBanner9, homeBottomBanner10]
    )

    return (
      <MainLayout>
        <View style={[styles.container]}>
          <DrawerStatus Icon={<DraftIcon color={Colors.orange} />} label="下書き" />
          <View style={styles.line}></View>
          <FlatList
            data={contacts}
            renderItem={ListItem}
            ListEmptyComponent={!isLoadingContacts ? <ContactListEmpty label="連絡はありません。" /> : undefined}
            refreshControl={
              <CustomRefreshControl
                refreshing={isLoadingContacts}
                onRefresh={() => {
                  refreshContacts()
                  refreshOrganizationDetail()
                }}
              />
            }
          />
        </View>
      </MainLayout>
    )
  }
)

DraftScreen.displayName = "DraftScreen"

export const draftScreenOptions: DrawerScreenOptions = {
  title: "下書き",
  drawerIcon: (props) => (
    <DrawerIconContainer>
      <DraftIcon {...props} color={Colors.orange} />
    </DrawerIconContainer>
  ),
  drawerLabel: () => <DrawerLabel title={"下書き"} showBadge={false} />,
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: Colors.white,
  },
  contentWrapper: {
    paddingHorizontal: 16,
    paddingTop: 10,
  },
  line: {
    width: "100%",
    height: 1,
    backgroundColor: Colors.white2,
  },
})
